import { collection, addDoc, query, where, orderBy, limit, getDocs, serverTimestamp, getDoc, doc } from 'firebase/firestore';
import { db, auth } from '../../../../../../firebase/config';
import { fetchBranchBankId } from '../../../../../branches/service/branchesService';

export type NewLocationSurveyFormData = {
    surveyId: string;
    surveyRequestDate: string;
    surveyDate: string;
    surveySubmittedOn: string;
    locationAddress: string;
    hazardousArea: string;
    spaceConsistsOf: string;
    directions: {
        front: string;
        left: string;
        back: string;
        right: string;
    };
    questions: {
        fuelStationAvailable: string;
        spaceForParking: string;
        spaceForVantagePoint: string;
        spaceForEmergencyExit: string;
        electricityIssue: string;
        ptclLineAvailable: string;
        otherAssessment?: string;
        otherAssessmentHazardous?: string;
    };
    remarks: string;
};

export type AssignLocationSurveyData = {
    surveyId: string;
    assignedToPsoId: string;
    assignedToPsoName: string;
    assignedToPsoCode?: string;
    assignedBy: string;
    assignedByName: string;
    locationAddress: string;
    bankId: string | null;
    surveyRequestDate?: string;
};

export async function generateSurveyId(bankId: string): Promise<string> {
    if (!bankId) {
        throw new Error('Bank ID is required to generate survey ID');
    }
    try {
        const today = new Date();
        const dateStr = today.toISOString().slice(0, 10).replace(/-/g, '');

        // Get the latest survey for today
        const surveysRef = collection(db, 'banks', bankId, 'locationSurveys');
        const q = query(
            surveysRef,
            where('surveyId', '>=', `LOC-SUR-${dateStr}-0000`),
            where('surveyId', '<=', `LOC-SUR-${dateStr}-9999`),
            orderBy('surveyId', 'desc'),
            limit(1)
        );

        const snapshot = await getDocs(q);
        let nextNumber = 1;

        if (!snapshot.empty) {
            const lastSurvey = snapshot.docs[0].data();
            const lastId = lastSurvey.surveyId;
            const lastNumber = parseInt(lastId.split('-').pop());
            nextNumber = lastNumber + 1;
        }

        const newSurveyId = `LOC-SUR-${dateStr}-${nextNumber.toString().padStart(4, '0')}`;
        return newSurveyId;
    } catch (error) {
        console.error('Error generating survey ID:', error);
        // Fallback to timestamp-based ID
        const timestamp = Date.now().toString().slice(-6);
        return `LOC-SUR-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${timestamp}`;
    }
}

export async function submitNewLocationSurvey(formData: NewLocationSurveyFormData, bankIdOverride?: string | null): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        throw new Error('User not authenticated');
    }

    let bankId: string | null | undefined = bankIdOverride;
    try {
        if (!bankId) {
            bankId = await fetchBranchBankId(uid);
        }
    } catch (error) {
        console.error('Error fetching bank ID for survey submission:', error);
    }

    if (!bankId) {
        throw new Error('Bank information is required to submit survey');
    }

    let assignedToName = '';
    let assignedToCode = '';
    let creatorName: string | undefined;

    if (bankId) {
        try {
            const psoDoc = await getDoc(doc(db, 'banks', bankId, 'pos', uid));
            if (psoDoc.exists()) {
                const data = psoDoc.data() as { name?: string; code?: string; employeeCode?: string; psoCode?: string; staffCode?: string };
                assignedToName = data.name || '';
                assignedToCode = data.code || data.employeeCode || data.psoCode || data.staffCode || '';
                creatorName = data.name || undefined;
            }
        } catch (error) {
            console.error('Error fetching PSO profile for survey submission:', error);
        }
    }

    const surveyData = {
        surveyId: formData.surveyId,
        surveyRequestDate: formData.surveyRequestDate,
        surveyDate: formData.surveyDate,
        surveySubmittedOn: formData.surveySubmittedOn,
        locationAddress: formData.locationAddress.trim(),
        hazardousArea: formData.hazardousArea.trim(),
        spaceConsistsOf: formData.spaceConsistsOf,
        directions: formData.directions,
        questions: formData.questions,
        remarks: formData.remarks.trim(),
        createdBy: uid,
        createdByName: creatorName,
        createdAt: new Date(),
        status: 'Submitted',
        bankId: bankId,
        assignedTo: uid,
        assignedToName: assignedToName,
        assignedToCode: assignedToCode,
        isHazardous: formData.questions.fuelStationAvailable === 'Yes' || formData.questions.otherAssessmentHazardous === 'Yes'
    };

    await addDoc(collection(db, 'banks', bankId, 'locationSurveys'), surveyData);
}

export async function assignNewLocationSurvey(data: AssignLocationSurveyData): Promise<void> {
    if (!data.bankId) {
        throw new Error('Bank information is required to assign survey');
    }

    const requestDate = data.surveyRequestDate || new Date().toISOString().split('T')[0];

    const payload = {
        surveyId: data.surveyId,
        assignedTo: data.assignedToPsoId,
        assignedToName: data.assignedToPsoName,
        assignedToCode: data.assignedToPsoCode || '',
        assignedBy: data.assignedBy,
        assignedByName: data.assignedByName,
        createdByName: data.assignedByName,
        locationAddress: data.locationAddress.trim(),
        bankId: data.bankId,
        status: 'Assigned',
        createdBy: data.assignedBy,
        surveyRequestDate: requestDate,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isHazardous: false
    };

    await addDoc(collection(db, 'banks', data.bankId, 'locationSurveys'), payload);
}
